import { img } from "../images";
import type { Blog } from "./types";

const DR_ARUN = "Dr. M. V. Arun Vijaiyan";
const ARUN_BIO =
  "Dr. M. V. Arun Vijaiyan is a Urologist and Andrologist at AV Clinic, Krishnagiri, with an M.S. and M.Ch. (Uro). He focuses on kidney stone care, urinary and prostate health, and male reproductive and sexual health, with a patient-centred approach to diagnosis and treatment.";
const ARUN_ROLE = "Urologist & Andrologist at AV Clinic, Krishnagiri";

const TEAM = "AV Clinic Medical Team";
const TEAM_BIO =
  "The AV Clinic Medical Team comprises the specialist doctors at AV Clinic, Krishnagiri, who provide patient-friendly health information across urology, andrology, dermatology and preventive care.";
const TEAM_ROLE = "Medical Team, AV Clinic, Krishnagiri";

export const infertilityBlogs: Blog[] = [
  {
    slug: "what-is-infertility",
    title: "What Is Infertility? A Simple Guide",
    category: "Infertility",
    excerpt:
      "Infertility is more common than many realise, and it is a couple's issue. Understand what it is and the first steps to take.",
    image: img.svcMaleInfertility,
    imageAlt: "Understanding infertility and the first steps to take",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-30",
    modified: "2026-08-30",
    readTime: "5 min read",
    tags: ["infertility", "couples", "fertility", "reproductive health"],
    primaryKeyword: "what is infertility",
    secondaryKeywords: ["infertility", "how to conceive", "fertility help Krishnagiri"],
    relatedService: "male-infertility",
    relatedServiceLabel: "Fertility Evaluation",
    relatedArticles: ["when-to-see-infertility-specialist", "infertility-tests-men", "infertility-tests-women"],
    sections: [
      {
        heading: "What infertility means",
        body: [
          "Infertility is the inability to conceive after a year of regular, unprotected intercourse (or six months if the woman is over 35). It is a medical condition, not a personal shortcoming, and it affects both partners.",
        ],
      },
      {
        heading: "It is a couple's issue",
        body: [
          "Infertility is often framed as a woman's problem, but male factors contribute in roughly half of cases. The right approach is to evaluate both partners together.",
        ],
      },
      {
        heading: "How common it is",
        body: [
          "Infertility is common, affecting many couples of reproductive age. The most important message is that most causes can be identified, and many can be treated.",
        ],
      },
      {
        heading: "The first step",
        body: [
          "The first step is a consultation with an infertility specialist, who will review your history and guide appropriate tests. Understanding the situation is the foundation of a clear plan.",
        ],
      },
    ],
    faqs: [
      {
        q: "When should a couple seek fertility help?",
        a: "Generally after a year of trying without conceiving, or six months if the woman is over 35. Earlier if there are known concerns.",
      },
      {
        q: "Is infertility only a woman's problem?",
        a: "No. Male factors contribute in a significant proportion of cases, so both partners should be assessed.",
      },
    ],
    seo: {
      title: "What Is Infertility? Causes & First Steps | AV Clinic",
      description:
        "Understand what infertility is, why it is a couple's issue and the first steps to seek help. Fertility guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "female-infertility-causes",
    title: "Common Causes of Female Infertility",
    category: "Women's Health",
    excerpt:
      "Several factors can affect a woman's fertility. Learn the common causes and how they are evaluated.",
    image: img.svcMaleInfertility,
    imageAlt: "Common causes of female infertility",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-26",
    modified: "2026-08-26",
    readTime: "5 min read",
    tags: ["female infertility", "PCOS", "endometriosis", "fertility"],
    primaryKeyword: "causes of female infertility",
    secondaryKeywords: ["female infertility", "PCOS fertility", "endometriosis fertility"],
    relatedService: undefined,
    relatedArticles: ["pcos-fertility", "endometriosis-fertility", "ovulation-fertility"],
    sections: [
      {
        heading: "Several factors can affect female fertility",
        body: [
          "A woman's fertility depends on ovulation, the health of the fallopian tubes and uterus, and hormonal balance. A problem in any of these can affect the chance of conceiving.",
        ],
      },
      {
        heading: "Common causes",
        body: ["Common causes of female infertility include:"],
        list: [
          "Ovulation disorders, such as in PCOS",
          "Blocked or damaged fallopian tubes",
          "Endometriosis",
          "Uterine conditions affecting implantation",
          "Age-related decline in egg quality",
        ],
      },
      {
        heading: "Why evaluation matters",
        body: [
          "Because the causes vary, proper evaluation is important. Simple tests can identify what is contributing and guide the right treatment.",
        ],
      },
      {
        heading: "A supportive path",
        body: [
          "Most causes of female infertility are treatable. A fertility specialist will explain your options clearly and support you at every step.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can PCOS affect fertility?",
        a: "Yes. PCOS is a common cause of ovulation problems and a frequent reason for difficulty conceiving, but it is often manageable.",
      },
      {
        q: "Does age affect female fertility?",
        a: "Yes. Fertility gradually declines with age, which is why acting earlier is often recommended.",
      },
    ],
    seo: {
      title: "Common Causes of Female Infertility | AV Clinic",
      description:
        "Learn the common causes of female infertility, including PCOS and endometriosis, and how they are evaluated. Fertility care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "male-factor-infertility",
    title: "Male Factor Infertility and Couple's Fertility",
    category: "Infertility",
    excerpt:
      "Male factors contribute to infertility in many couples. Understand how they are identified and why evaluating both partners matters.",
    image: img.svcMaleInfertility,
    imageAlt: "Male factor infertility and couple's fertility",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-08-22",
    modified: "2026-08-22",
    readTime: "5 min read",
    tags: ["male factor infertility", "sperm count", "fertility", "andrology"],
    primaryKeyword: "male factor infertility",
    secondaryKeywords: ["male factor", "sperm count and fertility", "couple fertility"],
    relatedService: "male-infertility",
    relatedServiceLabel: "Male Infertility",
    relatedArticles: ["sperm-count-fertility", "male-infertility-causes", "infertility-tests-men"],
    sections: [
      {
        heading: "A shared journey",
        body: [
          "Fertility involves both partners. When a couple struggles to conceive, evaluating both is the smart, efficient approach — male factors are involved in a large proportion of cases.",
        ],
      },
      {
        heading: "How male factors are checked",
        body: [
          "A semen analysis is the primary test, looking at sperm count, movement and shape. An andrologist also reviews medical history, lifestyle and any underlying conditions.",
        ],
      },
      {
        heading: "What male factor infertility means",
        body: [
          "Male factor infertility means a male-related reason contributes to the difficulty conceiving. It does not imply a personal failing and is often treatable.",
        ],
      },
      {
        heading: "Next steps",
        body: [
          "Once identified, most male factors can be addressed with lifestyle changes, medical treatment or surgical options where appropriate. A clear plan gives the couple confidence.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is male factor infertility common?",
        a: "Yes. Male factors contribute to a significant share of couple infertility, which is why both partners should be evaluated.",
      },
      {
        q: "What does a semen analysis show?",
        a: "It measures sperm count, movement and shape, giving a clear picture of male fertility.",
      },
    ],
    seo: {
      title: "Male Factor Infertility & Couple's Fertility | AV Clinic",
      description:
        "Understand male factor infertility and why evaluating both partners matters. Andrology and fertility care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "infertility-tests-men",
    title: "Infertility Tests for Men",
    category: "Infertility",
    excerpt:
      "Male fertility tests are simple, quick and painless. Learn what they involve and what results tell you.",
    image: img.svcSpermAnalysis,
    imageAlt: "Infertility tests for men",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-08-18",
    modified: "2026-08-18",
    readTime: "5 min read",
    tags: ["fertility tests", "semen analysis", "male fertility", "andrology"],
    primaryKeyword: "male fertility tests",
    secondaryKeywords: ["semen analysis", "male infertility test", "sperm test Krishnagiri"],
    relatedService: "sperm-analysis",
    relatedServiceLabel: "Sperm Analysis",
    relatedArticles: ["sperm-count-fertility", "male-infertility-causes", "what-is-infertility"],
    sections: [
      {
        heading: "Simple, quick and painless",
        body: [
          "Male fertility testing is straightforward. The main test is a semen analysis, which is quick and painless, and it gives valuable information right away.",
        ],
      },
      {
        heading: "What the tests cover",
        body: ["Common male fertility tests include:"],
        list: [
          "Semen analysis — sperm count, movement and shape",
          "Clinical examination",
          "Blood tests for hormonal levels, where needed",
          "Ultrasound in some cases to look at the scrotum",
        ],
      },
      {
        heading: "When to test",
        body: [
          "A semen analysis is usually recommended when a couple has been trying to conceive for a year without success (or six months if over 35), or earlier if there are known concerns.",
        ],
      },
      {
        heading: "What happens next",
        body: [
          "Based on the results, your specialist will discuss whether everything is normal or whether further evaluation or treatment is needed. It is a clear, supportive process.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is a semen analysis painful?",
        a: "No. It is a simple, painless test. Instructions are given to ensure accurate results.",
      },
      {
        q: "How many samples are needed?",
        a: "Often one sample is enough, though a repeat may be recommended to confirm findings.",
      },
    ],
    seo: {
      title: "Infertility Tests for Men | AV Clinic",
      description:
        "Learn about male fertility tests — semen analysis and more. Simple, painless testing for infertility at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "infertility-tests-women",
    title: "Infertility Tests for Women",
    category: "Women's Health",
    excerpt:
      "Female fertility testing helps identify what may be affecting conception. Learn what the common tests involve.",
    image: img.svcMaleInfertility,
    imageAlt: "Infertility tests for women",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-14",
    modified: "2026-08-14",
    readTime: "5 min read",
    tags: ["female fertility", "fertility tests", "ovulation", "reproductive health"],
    primaryKeyword: "female infertility tests",
    secondaryKeywords: ["fertility tests for women", "ovulation test", "fertility evaluation women"],
    relatedService: undefined,
    relatedArticles: ["ovulation-fertility", "female-infertility-causes", "when-to-see-infertility-specialist"],
    sections: [
      {
        heading: "Finding the cause together",
        body: [
          "Female fertility testing looks at ovulation, hormonal balance and the health of the uterus and fallopian tubes. These simple investigations help identify what may be affecting conception.",
        ],
      },
      {
        heading: "Common tests",
        body: ["Female fertility tests may include:"],
        list: [
          "Blood tests to check hormone levels",
          "Ultrasound to look at the ovaries and uterus",
          "Tests to check the fallopian tubes",
          "Tracking ovulation",
        ],
      },
      {
        heading: "Why it is worthwhile",
        body: [
          "Identifying the cause is key to the right treatment. Many causes are treatable, and a clear diagnosis gives you confidence in the way forward.",
        ],
      },
      {
        heading: "A supported process",
        body: [
          "Fertility testing is explained clearly at each step. Your specialist will guide you through the results and what they mean for your plan.",
        ],
      },
    ],
    faqs: [
      {
        q: "When should a woman have fertility tests?",
        a: "After a year of trying (or six months if over 35), or earlier if there are known concerns.",
      },
      {
        q: "Are fertility tests painful?",
        a: "Most are simple and quick. Some tests may cause mild discomfort, which your specialist will explain beforehand.",
      },
    ],
    seo: {
      title: "Infertility Tests for Women | AV Clinic",
      description:
        "Learn about female fertility testing — hormone tests, ultrasound and ovulation tracking. Fertility care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "when-to-see-infertility-specialist",
    title: "When Should You See an Infertility Specialist?",
    category: "Infertility",
    excerpt:
      "Knowing when to seek fertility help can make a real difference. Learn the right timing and why it matters.",
    image: img.svcUroConsult,
    imageAlt: "When to see an infertility specialist",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-10",
    modified: "2026-08-10",
    readTime: "5 min read",
    tags: ["infertility specialist", "fertility", "couples", "timing"],
    primaryKeyword: "when to see an infertility specialist",
    secondaryKeywords: ["infertility specialist Krishnagiri", "when to seek fertility help", "fertility clinic"],
    relatedService: "andrology-consultation",
    relatedServiceLabel: "Fertility Consultation",
    relatedArticles: ["what-is-infertility", "infertility-tests-men", "infertility-tests-women"],
    sections: [
      {
        heading: "Timing can make a difference",
        body: [
          "Fertility naturally declines with age, so knowing when to seek help is important. Acting earlier can simplify diagnosis and treatment.",
        ],
      },
      {
        heading: "When to seek help",
        body: ["Consider an infertility consultation if:"],
        list: [
          "You have been trying to conceive for a year without success",
          "The woman is over 35 and you have been trying for six months",
          "There is a known condition affecting fertility (such as PCOS or varicocele)",
          "You have irregular cycles, or a history that may affect fertility",
        ],
      },
      {
        heading: "Why both partners matter",
        body: [
          "Because fertility involves both partners, the specialist will assess both. This gives the fullest picture and the best plan.",
        ],
      },
      {
        heading: "A positive first step",
        body: [
          "A fertility consultation is about understanding, not blame. You will leave with clarity and a practical path forward.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need to try for a full year before seeking help?",
        a: "That is a general guide. If you are over 35, or have known concerns, seeking help earlier is sensible.",
      },
      {
        q: "Should my partner and I both be assessed?",
        a: "Yes. Evaluating both partners together gives the most complete picture.",
      },
    ],
    seo: {
      title: "When to See an Infertility Specialist | AV Clinic",
      description:
        "Learn when to see an infertility specialist in Krishnagiri — timing, signs and why both partners matter. Fertility care at AV Clinic.",
    },
  },
  {
    slug: "ovulation-fertility",
    title: "Understanding Ovulation and Fertility",
    category: "Women's Health",
    excerpt:
      "Ovulation is central to fertility. Learn how it works, how to track it and why it matters for conception.",
    image: img.svcMaleInfertility,
    imageAlt: "Understanding ovulation and fertility",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-06",
    modified: "2026-08-06",
    readTime: "5 min read",
    tags: ["ovulation", "fertility", "women's health", "conception"],
    primaryKeyword: "ovulation and fertility",
    secondaryKeywords: ["how to track ovulation", "ovulation window", "fertile days"],
    relatedService: undefined,
    relatedArticles: ["female-infertility-causes", "age-fertility", "lifestyle-fertility"],
    sections: [
      {
        heading: "What ovulation is",
        body: [
          "Ovulation is the release of an egg from the ovary, which usually happens once a month. It is the most fertile time of the cycle and is central to conceiving.",
        ],
      },
      {
        heading: "Tracking ovulation",
        body: ["You can identify your fertile window with simple methods:"],
        list: [
          "Tracking your cycle length",
          "Noting changes in cervical mucus",
          "Using ovulation predictor kits",
          "Tracking basal body temperature",
        ],
      },
      {
        heading: "The fertile window",
        body: [
          "The most fertile days are the few days before and the day of ovulation. Knowing your window helps plan the right timing.",
        ],
      },
      {
        heading: "When there is a problem",
        body: [
          "If cycles are irregular, very short or very long, or if ovulation may not be happening, a fertility evaluation can identify the cause and guide treatment.",
        ],
      },
    ],
    faqs: [
      {
        q: "How do I know when I ovulate?",
        a: "A combination of cycle tracking, cervical mucus changes and ovulation kits helps identify your likely fertile window.",
      },
      {
        q: "What if my cycles are irregular?",
        a: "Irregular cycles can affect ovulation and fertility. A specialist evaluation can help identify the cause.",
      },
    ],
    seo: {
      title: "Understanding Ovulation & Fertility | AV Clinic",
      description:
        "Learn how ovulation works, how to track your fertile window and why it matters. Women's fertility guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "lifestyle-fertility",
    title: "Lifestyle Factors That Affect Fertility",
    category: "Preventive Healthcare",
    excerpt:
      "Your daily habits influence both partners' fertility. Learn the lifestyle factors that matter and how to improve them.",
    image: img.svcMensHealth,
    imageAlt: "Lifestyle factors that affect fertility",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-08-02",
    modified: "2026-08-02",
    readTime: "5 min read",
    tags: ["fertility", "lifestyle", "diet", "preventive health"],
    primaryKeyword: "lifestyle factors and fertility",
    secondaryKeywords: ["lifestyle and fertility", "fertility tips", "healthy habits fertility"],
    relatedService: "mens-health",
    relatedServiceLabel: "Health Check-up",
    relatedArticles: ["sperm-health-lifestyle", "healthy-lifestyle-adults", "stress-fertility"],
    sections: [
      {
        heading: "Habits shape fertility",
        body: [
          "Fertility is influenced by overall health, which is shaped by lifestyle. Both partners can support their fertility with positive habits.",
        ],
      },
      {
        heading: "What to focus on",
        body: ["These factors matter for fertility:"],
        list: [
          "A healthy weight and regular exercise",
          "A balanced diet rich in fruit and vegetables",
          "Quitting smoking and limiting alcohol",
          "Managing stress and getting adequate sleep",
          "Limiting excess caffeine",
        ],
      },
      {
        heading: "Small changes, real impact",
        body: [
          "You do not need a perfect lifestyle. Even consistent small improvements support hormonal balance, sperm quality and overall fertility.",
        ],
      },
      {
        heading: "When to get support",
        body: [
          "If you have been trying for a while, combine lifestyle changes with a fertility evaluation. Professional guidance ensures you are on the right track.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long do lifestyle changes take to affect fertility?",
        a: "Some effects, such as on sperm quality, may take a few months. Others may be quicker. Consistency is key.",
      },
      {
        q: "Does weight affect fertility?",
        a: "Both underweight and overweight can affect hormonal balance and fertility. A healthy weight supports fertility.",
      },
    ],
    seo: {
      title: "Lifestyle Factors That Affect Fertility | AV Clinic",
      description:
        "Learn the lifestyle factors that affect fertility in both partners and how to improve them. Health guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "pcos-fertility",
    title: "PCOS and Fertility",
    category: "Women's Health",
    excerpt:
      "PCOS is a common cause of ovulation problems. Understand how it affects fertility and how it can be managed.",
    image: img.svcMaleInfertility,
    imageAlt: "PCOS and fertility",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-07-29",
    modified: "2026-07-29",
    readTime: "5 min read",
    popular: true,
    tags: ["PCOS", "polycystic ovary", "fertility", "women's health"],
    primaryKeyword: "PCOS and fertility",
    secondaryKeywords: ["PCOS", "ovulation problems", "PCOS treatment"],
    relatedService: undefined,
    relatedArticles: ["female-infertility-causes", "ovulation-fertility", "endometriosis-fertility"],
    sections: [
      {
        heading: "What PCOS is",
        body: [
          "Polycystic ovary syndrome (PCOS) is a hormonal condition that affects ovulation and can cause irregular periods, excess hair growth, acne and fertility challenges. It is very common.",
        ],
      },
      {
        heading: "How PCOS affects fertility",
        body: [
          "Because PCOS often causes irregular or absent ovulation, it can make it harder to conceive. But many women with PCOS do conceive with the right support.",
        ],
      },
      {
        heading: "Managing PCOS for fertility",
        body: ["Management may include:"],
        list: [
          "Lifestyle changes, including weight management and balanced nutrition",
          "Regular exercise to improve insulin sensitivity",
          "Medication to encourage ovulation, where appropriate",
          "Specialist guidance on timing and monitoring",
        ],
      },
      {
        heading: "A hopeful path",
        body: [
          "PCOS is one of the most treatable causes of ovulation-related infertility. With a tailored plan, many women achieve their goal of conceiving.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I get pregnant with PCOS?",
        a: "Yes. Many women with PCOS conceive with lifestyle changes, treatment and specialist support.",
      },
      {
        q: "Does PCOS affect ovulation?",
        a: "Yes. PCOS commonly affects ovulation, which is a key reason it can impact fertility.",
      },
    ],
    seo: {
      title: "PCOS and Fertility: What to Know | AV Clinic",
      description:
        "Understand PCOS, its effect on ovulation and fertility, and how it is managed. Women's health support at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "endometriosis-fertility",
    title: "Endometriosis and Fertility",
    category: "Women's Health",
    excerpt:
      "Endometriosis can affect fertility. Understand the condition, its symptoms and the options available.",
    image: img.svcMaleInfertility,
    imageAlt: "Endometriosis and fertility",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-07-25",
    modified: "2026-07-25",
    readTime: "5 min read",
    tags: ["endometriosis", "pelvic pain", "fertility", "women's health"],
    primaryKeyword: "endometriosis and fertility",
    secondaryKeywords: ["endometriosis", "endometriosis symptoms", "endometriosis treatment"],
    relatedService: undefined,
    relatedArticles: ["female-infertility-causes", "pcos-fertility", "symptoms-not-to-ignore"],
    sections: [
      {
        heading: "What endometriosis is",
        body: [
          "Endometriosis is a condition in which tissue similar to the lining of the uterus grows outside the uterus. It can cause significant pain and can affect fertility.",
        ],
      },
      {
        heading: "Symptoms",
        body: ["Common symptoms include:"],
        list: [
          "Severe period pain",
          "Pain during intercourse",
          "Heavy or irregular periods",
          "Pelvic pain not related to periods",
          "Difficulty conceiving",
        ],
      },
      {
        heading: "How it affects fertility",
        body: [
          "Endometriosis can affect fertility by causing inflammation, scarring and changes to the reproductive organs. The severity varies from person to person.",
        ],
      },
      {
        heading: "Getting help",
        body: [
          "If you have symptoms or difficulty conceiving, a fertility evaluation can explore the condition. A specialist can discuss treatment and fertility options suited to you.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I conceive with endometriosis?",
        a: "Yes. Many women with endometriosis conceive, though they may need specialist support. The approach depends on the individual case.",
      },
      {
        q: "Does endometriosis always cause infertility?",
        a: "No. Some women have endometriosis and conceive easily, while others face difficulty. It varies by person.",
      },
    ],
    seo: {
      title: "Endometriosis & Fertility: What to Know | AV Clinic",
      description:
        "Understand endometriosis, its symptoms and how it can affect fertility. Women's health support at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "age-fertility",
    title: "Age and Fertility",
    category: "Women's Health",
    excerpt:
      "Age is a key factor in fertility for both partners. Understand how age affects fertility and what it means for planning.",
    image: img.svcMaleInfertility,
    imageAlt: "How age affects fertility",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-07-21",
    modified: "2026-07-21",
    readTime: "5 min read",
    tags: ["age and fertility", "fertility", "family planning", "women's health"],
    primaryKeyword: "age and fertility",
    secondaryKeywords: ["fertility and age", "age and conception", "fertility over 35"],
    relatedService: undefined,
    relatedArticles: ["what-is-infertility", "ovulation-fertility", "when-to-see-infertility-specialist"],
    sections: [
      {
        heading: "Fertility changes with age",
        body: [
          "Fertility naturally declines with age. For women, egg quantity and quality decline especially after the mid-30s. For men, sperm quality can also be affected by age and health.",
        ],
      },
      {
        heading: "What the numbers show",
        body: [
          "The chance of conceiving is highest for women in their 20s and early 30s, then gradually declines. Male fertility also changes, though less dramatically and more gradually.",
        ],
      },
      {
        heading: "Planning with this in mind",
        body: [
          "Understanding age-related fertility helps you plan. If you are trying to conceive and are over 35, seeking help sooner is wise.",
        ],
      },
      {
        heading: "It is not a deadline",
        body: [
          "Many people conceive in their late 30s and 40s with the right support. Being informed helps you make confident choices rather than feeling pressured.",
        ],
      },
    ],
    faqs: [
      {
        q: "At what age does female fertility decline?",
        a: "Fertility is highest in the 20s and early 30s, with a more noticeable decline after 35 and further after 40.",
      },
      {
        q: "Does age affect male fertility?",
        a: "Sperm quality can be affected by age and health, though the effect is generally more gradual than in women.",
      },
    ],
    seo: {
      title: "Age and Fertility: What to Know | AV Clinic",
      description:
        "Understand how age affects fertility for both partners and how to plan with confidence. Fertility guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "fertility-myths-facts",
    title: "Fertility Myths and Facts",
    category: "Infertility",
    excerpt:
      "There are many myths around fertility. Separate the facts from the fiction with this clear guide.",
    image: img.svcMaleInfertility,
    imageAlt: "Common fertility myths and facts",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-07-17",
    modified: "2026-07-17",
    readTime: "4 min read",
    tags: ["fertility myths", "misconceptions", "reproductive health", "education"],
    primaryKeyword: "fertility myths",
    secondaryKeywords: ["fertility myths and facts", "infertility misconceptions", "fertility truth"],
    relatedService: undefined,
    relatedArticles: ["what-is-infertility", "lifestyle-fertility", "stress-fertility"],
    sections: [
      {
        heading: "Why myths are harmful",
        body: [
          "Fertility myths can create unnecessary stress and delay the right help. Clearing up misconceptions helps couples make informed decisions.",
        ],
      },
      {
        heading: "Common myths and the facts",
        body: ["Here are a few common beliefs and the truth behind them:"],
        list: [
          "Myth: Infertility is usually a woman's problem — Fact: male factors contribute in roughly half of cases.",
          "Myth: You can only get pregnant on the day you ovulate — Fact: the fertile window spans several days before and on ovulation.",
          "Myth: Stress causes infertility — Fact: stress can affect it, but it is rarely the sole cause.",
          "Myth: You must try for years before seeking help — Fact: seeking help after a year (or six months over 35) is standard.",
        ],
      },
      {
        heading: "The reliable path",
        body: [
          "The most reliable way to understand your fertility is an evaluation with a specialist, not guesswork or internet advice.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is infertility always the woman's fault?",
        a: "No. Male factors contribute in a significant share of cases, so both partners should be assessed.",
      },
      {
        q: "Can stress alone cause infertility?",
        a: "Stress can affect fertility, but it is rarely the only factor. A proper evaluation is the reliable approach.",
      },
    ],
    seo: {
      title: "Fertility Myths and Facts | AV Clinic",
      description:
        "Separate fertility myths from the facts — from who is 'responsible' to when to seek help. Fertility guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "fertility-treatment-options",
    title: "Understanding Fertility Treatment Options",
    category: "Infertility",
    excerpt:
      "Fertility treatment has come a long way. Learn the main options available and how the right one is chosen.",
    image: img.svcMaleInfertility,
    imageAlt: "Understanding fertility treatment options",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-07-13",
    modified: "2026-07-13",
    readTime: "6 min read",
    tags: ["fertility treatment", "IVF", "IUI", "infertility"],
    primaryKeyword: "fertility treatment options",
    secondaryKeywords: ["fertility treatment Krishnagiri", "IVF", "IUI", "infertility treatment"],
    relatedService: "male-infertility",
    relatedServiceLabel: "Fertility Treatment",
    relatedArticles: ["what-is-infertility", "male-infertility-treatment", "when-to-see-infertility-specialist"],
    sections: [
      {
        heading: "Treatment is tailored to you",
        body: [
          "Fertility treatment is not one-size-fits-all. The right option depends on the cause, the age of both partners and your preferences.",
        ],
      },
      {
        heading: "Common options",
        body: ["Treatment options may include:"],
        list: [
          "Ovulation induction, to encourage or regulate ovulation",
          "Intrauterine insemination (IUI)",
          "In vitro fertilisation (IVF)",
          "Surgical care for conditions such as varicocele",
          "Treatment of underlying health conditions",
        ],
      },
      {
        heading: "How the choice is made",
        body: [
          "The selection of option is based on a thorough evaluation and a clear discussion of benefits, timing and considerations. Your specialist will guide you.",
        ],
      },
      {
        heading: "A supportive journey",
        body: [
          "Fertility treatment can be emotional. Good counselling and clear communication make the journey manageable and hopeful.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is the first step in fertility treatment?",
        a: "A careful evaluation of both partners, followed by a discussion of the most suitable option for your case.",
      },
      {
        q: "Does fertility treatment guarantee success?",
        a: "No treatment guarantees success, but many options significantly improve the chance of conceiving. Your specialist will set realistic expectations.",
      },
    ],
    seo: {
      title: "Fertility Treatment Options Explained | AV Clinic",
      description:
        "Understand common fertility treatment options — ovulation induction, IUI and IVF — and how the right one is chosen. Fertility care at AV Clinic.",
    },
  },
  {
    slug: "sperm-count-fertility",
    title: "Male Fertility and Sperm Count",
    category: "Men's Health",
    excerpt:
      "Sperm count is one factor in male fertility. Understand what it means, what affects it and when to check.",
    image: img.svcSpermAnalysis,
    imageAlt: "Male fertility and sperm count",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-09",
    modified: "2026-07-09",
    readTime: "5 min read",
    tags: ["sperm count", "male fertility", "semen analysis", "andrology"],
    primaryKeyword: "sperm count and fertility",
    secondaryKeywords: ["low sperm count", "sperm count test", "male fertility"],
    relatedService: "sperm-analysis",
    relatedServiceLabel: "Sperm Analysis",
    relatedArticles: ["male-infertility-causes", "infertility-tests-men", "sperm-health-lifestyle"],
    sections: [
      {
        heading: "Sperm count is one part of the picture",
        body: [
          "Male fertility depends on sperm count, movement (motility) and shape (morphology). A good count is helpful, but movement and shape matter too.",
        ],
      },
      {
        heading: "What affects sperm count",
        body: ["Several factors can lower sperm count:"],
        list: [
          "Varicocele",
          "Hormonal imbalances",
          "Lifestyle factors such as smoking and excess alcohol",
          "Heat exposure, obesity and stress",
          "Certain medical conditions and medications",
        ],
      },
      {
        heading: "Checking sperm count",
        body: [
          "A semen analysis measures sperm count, movement and shape. It is the standard first test for male fertility and is simple to do.",
        ],
      },
      {
        heading: "What a result means",
        body: [
          "A low count is not the end of the road — it is the start of finding the cause and planning appropriate care. Many causes are treatable.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is a normal sperm count?",
        a: "A semen analysis gives a range rather than a single number. Your specialist will interpret the results in context.",
      },
      {
        q: "Can sperm count improve?",
        a: "Yes, often. Treating the cause and improving lifestyle can raise sperm quality and count.",
      },
    ],
    seo: {
      title: "Male Fertility & Sperm Count | AV Clinic",
      description:
        "Understand sperm count, what affects it and how it is checked. Male fertility and sperm health at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "stress-fertility",
    title: "How Stress Can Affect Fertility",
    category: "Women's Health",
    excerpt:
      "Stress and fertility are linked. Learn how stress can affect conception and practical ways to manage it.",
    image: img.svcMensHealth,
    imageAlt: "How stress affects fertility",
    author: TEAM,
    authorBio: TEAM_BIO,
    authorRole: TEAM_ROLE,
    date: "2026-07-05",
    modified: "2026-07-05",
    readTime: "4 min read",
    tags: ["stress", "fertility", "mental health", "wellbeing"],
    primaryKeyword: "stress and fertility",
    secondaryKeywords: ["stress affecting fertility", "manage stress", "coping with infertility"],
    relatedService: "mens-health",
    relatedServiceLabel: "Health Check-up",
    relatedArticles: ["stress-management-health", "lifestyle-fertility", "fertility-myths-facts"],
    sections: [
      {
        heading: "The stress–fertility link",
        body: [
          "Stress does not directly cause infertility, and it is rarely the only factor. But it can affect hormonal balance, sleep and habits in ways that influence fertility.",
        ],
      },
      {
        heading: "How stress can interfere",
        body: ["High stress may:"],
        list: [
          "Affect hormone balance and ovulation",
          "Reduce libido and frequency of intercourse",
          "Disrupt sleep and eating habits",
          "Increase the emotional burden of trying to conceive",
        ],
      },
      {
        heading: "Practical ways to manage it",
        body: [
          "Coping with the stress of trying to conceive is important. Consider steady support, gentle exercise, mindfulness and talking to people you trust or a counsellor.",
        ],
      },
      {
        heading: "It is not your fault",
        body: [
          "Feeling stressed about fertility is completely understandable. Reaching out for support is a healthy, positive step — not a sign of weakness.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can stress cause infertility?",
        a: "Stress can affect fertility, but it is rarely the sole cause. A proper evaluation is the reliable approach.",
      },
      {
        q: "Does counselling help with fertility stress?",
        a: "Yes. Counselling can help you manage the emotional strain and is often a valuable part of fertility care.",
      },
    ],
    seo: {
      title: "How Stress Affects Fertility | AV Clinic",
      description:
        "Understand how stress can affect fertility and practical ways to manage it. Fertility and wellbeing guidance at AV Clinic, Krishnagiri.",
    },
  },
];